import _styled from "styled-components";
const _excluded = ["className", "style", "disabled", "isLoading", "error", "isOpen", "getLabelProps", "getToggleButtonProps", "isAutocomplete", "children", "highDensity", "highlightButtonOnFocus", "onClick"],
  _excluded2 = ["ref"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import PropTypes from 'prop-types';
import { useDeprecate } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, { getInteractiveStyles, stylePropType } from '@adsk/alloy-react-theme';
import ToggleButton from './ToggleButton';
import { stripUnknownProps } from '../../helpers/internalHelpers';
const ControlLabelWrapped = (_ref, ref) => {
  let {
      className,
      style,
      disabled = false,
      isLoading = false,
      error,
      isOpen = false,
      getLabelProps,
      getToggleButtonProps,
      isAutocomplete,
      children,
      highDensity,
      highlightButtonOnFocus,
      onClick
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  useDeprecate(!!getLabelProps, 'Dropdown Control', 'getLabelProps', 'an external <label> element and apply `getLabelProps` to it');

  // if this is not autocomplete, the ref gets provided to getToggleButtonProps to merge them
  const _ref2 = isAutocomplete ? {
      ref
    } : getToggleButtonProps({
      // TODO: remove this cast once this is merged https://github.com/downshift-js/downshift/pull/1486
      ref: ref,
      disabled
    }),
    {
      ref: divRef
    } = _ref2,
    divProps = _objectWithoutProperties(_ref2, _excluded2);
  const controlLabelProps = stripUnknownProps(props, ['openMenu', 'selectItem', 'selectedItem', 'itemToString']);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    ref: divRef,
    "data-testid": 'Dropdown__control',
    className: className,
    onClick: disabled ? undefined : onClick
  }, divProps, controlLabelProps, {
    $_css: [_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: "1px ".concat(theme.spacing.S, "px"),
      border: "1px solid ".concat(theme.tokens.colors.border.value),
      borderRadius: theme.borderRadius.small,
      cursor: 'pointer'
    }, getInteractiveStyles(disabled)), isOpen && {
      borderColor: theme.tokens.colors.border.focused.value,
      boxShadow: theme.halos.focus,
      '&:hover': {
        boxShadow: theme.halos.focus
      }
    }), disabled && {
      backgroundColor: theme.tokens.colors.background.neutral.inactive.value,
      borderColor: theme.tokens.colors.border.inactive.value,
      color: theme.tokens.colors.text.inactive.value,
      cursor: 'not-allowed'
    }), error && !disabled && {
      borderColor: theme.tokens.colors.border.error.value
    }), style]
  }), children, isLoading && /*#__PURE__*/React.createElement(ProgressRing, {
    size: ProgressRing.SIZES.XSMALL,
    style: {
      marginLeft: highDensity ? 0 : theme.spacing.XS
    }
  }), /*#__PURE__*/React.createElement(ToggleButton, {
    isOpen: isOpen,
    disabled: disabled,
    getToggleButtonProps: isAutocomplete ? getToggleButtonProps : undefined,
    style: _objectSpread({
      marginLeft: highDensity ? 0 : theme.spacing.XS,
      marginRight: theme.spacing.XXS
    }, disabled && {
      color: theme.tokens.colors.icon.inactive.value
    }),
    highlightButtonOnFocus: highlightButtonOnFocus
  }));
};
const ControlLabel = /*#__PURE__*/React.forwardRef(ControlLabelWrapped);
ControlLabel.displayName = 'ControlLabel';
ControlLabel.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** Function to get props for the label surrounding the control */
  getLabelProps: PropTypes.func,
  /** The content of te ControlContainer */
  children: PropTypes.any
};
export default Object.assign(ControlLabel, {
  propTypes: ControlLabel.propTypes
});
var _StyledDiv = _styled("div").withConfig({
  displayName: "ControlLabel___StyledDiv",
  componentId: "sc-qqpdvw-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=ControlLabel.js.map